import React, { useState, useEffect } from 'react';

import EthereumAddressTextField from '../component/EthereumAddressTextField';
import EtherValueTextField from '../component/EtherValueTextField';
import FieldSetFormControl from '../component/FieldSetFormControl';
import HashTextField from '../component/HashTextField';
import HexStringTextField from '../component/HexStringTextField';
import BigIntegerTextField from '../component/BigIntegerTextField';

import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import MenuItem from "@material-ui/core/MenuItem";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import TextField from "@material-ui/core/TextField";

import BN from 'bn.js';
import { ethers } from 'ethers';

function toBN (x) {
  x = x.trim ().toLowerCase ();
  if (!x) throw new Error ("Value is empty");
  return x.startsWith ('0x') ? new BN (x.substring (2), 16) :
      x.startsWith ('0b') ? new BN (x.substring (2), 2) :
      new BN (x);
}

function toHex (x) {
  let hex = toBN (x).toString (16);
  if (hex.length % 2 === 1) hex = '0' + hex;
  return ethers.utils.hexlify ('0x' + hex);
}

function clean (x) {
  Object.keys(x).forEach(key => {
    if (x[key] === undefined) {
      delete x[key];
    }
  });
  return x;
}

function Transaction (props) {
  const {
    ...other
  } = props;

  const [ mode, setMode ] = useState ('encode');
  const [ to, setTo ] = useState ('');
  const [ valueUnit, setValueUnit ] = useState ('ether');
  const [ value, setValue ] = useState ('0');
  const [ data, setData ] = useState ('0x');
  const [ nonce, setNonce ] = useState ('0');
  const [ gas, setGas ] = useState ('21000');
  const [ eip1559, setEIP1559 ] = useState (false);
  const [ gasPrice, setGasPrice ] = useState ('1000000000');
  const [ maxFeePerGas, setMaxFeePerGas ] = useState ('1000000000');
  const [ maxPriorityFeePerGas, setMaxPriorityFeePerGas ] = useState ('1000000000');
  const [ signed, setSigned ] = useState (false);
  const [ chainID, setChainID ] = useState (false);
  const [ customChainID, setCustomChainID ] = useState ('');
  const [ v, setV ] = useState ('');
  const [ r, setR ] = useState ('');
  const [ s, setS ] = useState ('');
  const [ from, setFrom ] = useState ('');
  const [ hash, setHash ] = useState ('');
  const [ rawTransaction, setRawTransaction ] = useState ('');

  const [ chains, setChains ] = useState ([]);

  useEffect (() => {
    const xmlHttpRequest = new XMLHttpRequest ();
    xmlHttpRequest.open ('GET', 'https://chainid.network/chains.json');
    xmlHttpRequest.send ();
    xmlHttpRequest.addEventListener ('load', event => {
      setChains (JSON.parse (xmlHttpRequest.responseText).sort ((a, b) => a.chainId - b.chainId));
    });

    return () => xmlHttpRequest.abort ();
  }, []);

  useEffect (() => {
    if (mode === 'encode' && signed) {
      try {
        const vBN = toBN (v);
  
        const effectiveChainID =
          vBN.lt (new BN (35)) ? undefined : vBN.sub (new BN (35)).div (new BN (2));
  
        if (effectiveChainID && chains.find (chain => chain.chainId.toString () === effectiveChainID.toString ())) {
          setChainID (effectiveChainID.toString ());
          setCustomChainID ('');
        } else if (effectiveChainID) {
          setChainID ('custom');
          setCustomChainID (effectiveChainID.toString ());
        } else {
          setChainID (false);
          setCustomChainID ('');
        }
      } catch (error) {
        setChainID (false);
        setCustomChainID ('');
      }
    }
  }, [ mode, signed, v, chains ]);

  useEffect (() => {
    if (mode === 'encode') {
      try {
        setRawTransaction (
          ethers.utils.serializeTransaction (clean ({
            type: eip1559 ? 2 : undefined,
            to: to.trim (),
            value: ethers.utils.parseUnits (value.trim (), valueUnit),
            data: data.trim (),
            nonce: toHex (nonce),
            gasLimit: toHex (gas),
            gasPrice: !eip1559 ? toHex (gasPrice) : undefined,
            maxFeePerGas: eip1559 ? toHex (maxFeePerGas) : undefined,
            maxPriorityFeePerGas: eip1559 ? toHex (maxPriorityFeePerGas) : undefined,
            chainId: chainID ? toHex (chainID === 'custom' ? customChainID : chainID.toString ()) : undefined
          }),
          signed ? {
            v: toHex (v),
            r: toHex (r),
            s: toHex (s)
          } : undefined));
      } catch (error) {
        setRawTransaction ('');
      }
      setFrom ('');
      setHash ('');
    } else {
      try {
        const tx = ethers.utils.parseTransaction (rawTransaction);

        setTo (tx.to || '');
        setValue (ethers.utils.formatUnits (tx.value, valueUnit).replace(/\.0*$/, ''));
        setData (tx.data);
        setNonce (tx.nonce.toString ());
        setGas (tx.gasLimit.toString ());
        setEIP1559 (!tx.gasPrice);
        setGasPrice (tx.gasPrice ? tx.gasPrice.toString () : '');
        setMaxFeePerGas (tx.maxFeePerGas ? tx.maxFeePerGas.toString () : '');
        setMaxPriorityFeePerGas (tx.maxPriorityFeePerGas ? tx.maxPriorityFeePerGas.toString () : '');
        setSigned (!!tx.hash);
        if (tx.chainId && chains.find (chain => chain.chainId.toString () === tx.chainId.toString ())) {
          setChainID (tx.chainId.toString ());
          setCustomChainID ('');
        } else if (tx.chainId) {
          setChainID ('custom');
          setCustomChainID (tx.chainId.toString ());
        } else {
          setChainID (false);
          setCustomChainID ('');
        }
        setV (tx.hash ? tx.v.toString () : '');
        setR (tx.hash ? tx.r.toString () : '');
        setS (tx.hash ? tx.s.toString () : '');
        setFrom (tx.hash ? tx.from.toString () : '');
        setHash (tx.hash ? tx.hash.toString () : '');
      } catch (error) {
        setTo ('');
        setValue ('');
        setData ('');
        setNonce ('');
        setGas ('');
        setEIP1559 (false);
        setGasPrice ('');
        setMaxFeePerGas ('');
        setMaxPriorityFeePerGas ('');
        setSigned (false);
        setChainID (false);
        setV ('');
        setR ('');
        setS ('');
        setFrom ('');
        setHash ('');
      }
    }
  }, [ mode, to, value, valueUnit, data, nonce, gas, eip1559, gasPrice, maxFeePerGas, maxPriorityFeePerGas, signed, chainID, customChainID, v, r, s, rawTransaction, chains ]);

  const decoded = (
    <Box component={FieldSetFormControl}
         fullWidth>
      <Box my={1}
           component={EthereumAddressTextField}
           label="To"
           mandatory={false}
           readOnly={mode === 'decode'}
           value={to}
           onChange={event => setTo (event.target.value)} />
      <Box my={1}
           component={EtherValueTextField}
           label="Value"
           readOnly={mode === 'decode'}
           value={value}
           unit={valueUnit}
           onChange={event => setValue (event.target.value)}
           onUnitChange={setValueUnit} />
      <Box my={1}
           component={HexStringTextField}
           label="Data"
           readOnly={mode === 'decode'}
           value={data}
           onChange={event => setData (event.target.value)} />
      <Box mx={-1}
           component={FormGroup}
           row>
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Nonce"
             readOnly={mode === 'decode'}
             value={nonce}
             onChange={event => setNonce (event.target.value)} />
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Gas"
             readOnly={mode === 'decode'}
             value={gas}
             onChange={event => setGas (event.target.value)} />
      </Box>
      <Box mx={-1}
           component={FormGroup}
           row>
        <Box alignSelf="flex-start"
             px={1}
             mt={2}
             component={FormControlLabel}
             label="EIP-1559"
             control={
               <Checkbox disabled={mode === 'decode'}
                         checked={eip1559}
                         onChange={event => setEIP1559 (event.target.checked)}/>
             } />
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Gas price"
             readOnly={mode === 'decode'}
             disabled={eip1559}
             value={gasPrice}
             onChange={event => setGasPrice (event.target.value)} />
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Max gas price"
             readOnly={mode === 'decode'}
             disabled={!eip1559}
             value={maxFeePerGas}
             onChange={event => setMaxFeePerGas (event.target.value)} />
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Max priority gas price"
             readOnly={mode === 'decode'}
             disabled={!eip1559}
             value={maxPriorityFeePerGas}
             onChange={event => setMaxPriorityFeePerGas (event.target.value)} />
      </Box>
      <Box mx={-1}
           component={FormGroup}
           row>
        <Box alignSelf="flex-start"
             px={1}
             mt={2}
             component={FormControlLabel}
             label="Signed"
             control={
               <Checkbox disabled={mode === 'decode'}
                         checked={signed}
                         onChange={event => setSigned (event.target.checked)}/>
             } />
        <Box m={1}
             flexGrow={1}
             component={TextField}
             label="Chain ID"
             helperText=" "
             select
             value={chainID}
             InputProps={{
               readOnly: signed || mode === 'decode'
             }}
             variant="outlined"
             onChange={event => setChainID (event.target.value)}>
          <MenuItem value={false}>
            None
          </MenuItem>
          <MenuItem value='custom'>
            Custom
          </MenuItem>
          <Divider />
          {chains.map ((chain, index) =>
            <MenuItem key={index} value={chain.chainId}>
              {chain.name} ({chain.chainId})
            </MenuItem>
          )}
        </Box>
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Custom chain ID"
             readOnly={signed || mode === 'decode'}
             disabled={chainID !== 'custom'}
             value={customChainID}
             onChange={event => setCustomChainID (event.target.value)} />
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="V"
             readOnly={mode === 'decode'}
             disabled={!signed}
             value={v}
             onChange={event => setV (event.target.value)} />
      </Box>
      <Box my={1}
           component={HexStringTextField}
           label="R"
           readOnly={mode === 'decode'}
           disabled={!signed}
           rows={0}
           value={r}
           onChange={event => setR (event.target.value)} />
      <Box my={1}
           component={HexStringTextField}
           label="S"
           readOnly={mode === 'decode'}
           disabled={!signed}
           rows={0}
           value={s}
           onChange={event => setS (event.target.value)} />
      <Box my={1}
           component={EthereumAddressTextField}
           label="From"
           disabled={!signed || mode === 'encode'}
           readOnly
           value={from} />
      <Box my={1}
           component={HashTextField}
           label="Hash"
           readOnly
           disabled={!signed || mode === 'encode'}
           value={hash} />
    </Box>
  );

  const encoded = (
    <Box component={FieldSetFormControl}
         fullWidth>
      <Box my={1}
           component={HexStringTextField}
           label="Raw transaction"
           readOnly={mode === 'encode'}
           value={rawTransaction}
           onChange={event => setRawTransaction (event.target.value)} />
    </Box>
  );

  return (
    <Box my={-1}
         component={FieldSetFormControl}
         fullWidth 
         {...other}>
      <Box my={1}
           component={RadioGroup}
           row
           value={mode}
           onChange={event => setMode (event.target.value)}>
        <FormControlLabel label="Encode"
                          control={<Radio />}
                          value="encode" />
        <FormControlLabel label="Decode"
                          control={<Radio />}
                          value="decode" />
      </Box>
      { mode === 'encode' ? decoded : encoded }
      <Box mt={1}
           mb={3}>
        <Divider />
      </Box>
      { mode === 'encode' ? encoded : decoded }
    </Box>
  );
}

export default Transaction;
